import classNames from 'clsx';
import { format } from 'date-fns';
import React from 'react';

import { PrizeCurrency, TimedStatus } from '@swf/casino';
import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import Translation from '@swf/components/Translation/Translation';
import type { Standing } from '@swf/core/types/CasinoRaces';
import type { TranslationTag } from '@swf/core/types/i18n';

import {
  TXTMOB_Min_BetX,
  TXTWCL_Ends_in_N0,
  TXTWCL_Round_Size,
  TXTWCL_Starts_in_N0,
  TXTWCL_Your_PrizeX,
  TXTWCL_Your_RankX
} from './RaceDetails.messages.json';
import styles from './RaceDetails.scss';

export interface Props {
  minBet?: number;
  roundSize?: number;
  completed: boolean;
  running: boolean;
  endTime: number;
  startTime: number;
  count: number;
  userRaceStanding?: Standing;
  isModal?: boolean;
  showRequirements: boolean;
  featured?: boolean;
}

const RaceRequirements: React.FC<{ text: TranslationTag; value: number }> = ({ text, value }) => (
  <div className={styles.item}>
    <Translation {...text} />
    <br />
    <span>{value}</span>
  </div>
);

const MinBet: React.FC<{ split?: boolean; value: number }> = ({ split = true, value }) => (
  <div className={classNames(styles.item)}>
    <Translation {...TXTMOB_Min_BetX} />
    {split && <br />}
    <span>
      <CurrencyAmount value={value} style="decimal" />
    </span>
  </div>
);

const RaceDetails: React.FC<Props> = ({
  minBet,
  roundSize,
  completed,
  running,
  endTime,
  startTime,
  count,
  userRaceStanding,
  isModal,
  showRequirements,
  featured = false
}) => (
  <>
    {userRaceStanding && (
      <div className={classNames(styles.yourInfo, isModal && styles.modal)}>
        <div>
          <Translation {...TXTWCL_Your_RankX} /> {userRaceStanding.rank || '-'}
        </div>
        <div>
          <Translation {...TXTWCL_Your_PrizeX} />
          &nbsp;
          <PrizeCurrency
            type={userRaceStanding.userRacePrize.type}
            currency={userRaceStanding.userRacePrize.currency}
            amount={userRaceStanding.userRacePrize.amount}
            showCurrency
          />
        </div>
      </div>
    )}
    {showRequirements && (
      <div
        className={classNames(styles.detailsBox, {
          [styles.modal]: isModal,
          [styles.featured]: featured
        })}
      >
        {!!minBet && <MinBet value={minBet} />}
        {!!roundSize && <RaceRequirements text={TXTWCL_Round_Size} value={roundSize} />}
      </div>
    )}
    <div
      className={classNames(styles.gameItemTime, {
        [styles.featured]: featured,
        [styles.modal]: isModal,
        [styles.completed]: completed
      })}
    >
      {!!minBet && !isModal && <MinBet value={minBet} split={false} />}
      <div className={classNames(styles.info)}>
        {completed ? (
          format(endTime, 'dd/LL/yyyy - HH:mm')
        ) : (
          <Translation
            {...(running ? TXTWCL_Ends_in_N0 : TXTWCL_Starts_in_N0)}
            values={{
              0: (
                <TimedStatus
                  completed={completed}
                  running={running}
                  endTime={endTime}
                  startTime={startTime}
                  count={count}
                  urgentCount
                />
              )
            }}
          />
        )}
      </div>
    </div>
  </>
);

export default RaceDetails;
